<script setup lang="ts">
import { useTourProgress } from '../composables/useTourProgress'

const { totalPercent, completedTours, totalTours, allComplete, modules } = useTourProgress()
</script>

<template>
  <div class="tpw">
    <div class="tpw__total">
      <div class="tpw__total-head">
        <span class="tpw__total-label">
          <template v-if="allComplete">
            <i class="fa-solid fa-trophy" /> Fully trained
          </template>
          <template v-else>Overall progress</template>
        </span>
        <span class="tpw__total-count">{{ completedTours }}/{{ totalTours }}</span>
      </div>
      <div class="tpw__track">
        <div
          class="tpw__fill"
          :class="{ 'tpw__fill--done': allComplete }"
          :style="{ width: `${totalPercent}%` }"
        />
      </div>
    </div>

    <ul class="tpw__modules">
      <li v-for="m in modules" :key="m.module" class="tpw__module">
        <div class="tpw__module-head">
          <span class="tpw__module-name">
            <i v-if="m.percent === 100" class="fa-solid fa-circle-check tpw__module-done" />
            {{ m.module }}
          </span>
          <span class="tpw__module-count">{{ m.completed }}/{{ m.total }}</span>
        </div>
        <div class="tpw__track tpw__track--sm">
          <div class="tpw__fill" :style="{ width: `${m.percent}%` }" />
        </div>
      </li>
    </ul>
  </div>
</template>

<style scoped lang="scss">
.tpw {
  &__total {
    margin-bottom: 16px;
  }

  &__total-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  &__total-label {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 0.9rem;
    font-weight: 600;

    i {
      color: rgb(var(--v-theme-success));
    }
  }

  &__total-count {
    font-family: 'Geist Mono Variable', monospace;
    font-size: 0.85rem;
    color: rgba(var(--v-theme-on-surface), 0.6);
  }

  &__track {
    height: 8px;
    border-radius: 999px;
    background: rgba(var(--v-theme-on-surface), 0.1);
    overflow: hidden;

    &--sm {
      height: 5px;
    }
  }

  &__fill {
    height: 100%;
    border-radius: 999px;
    background: rgb(var(--v-theme-primary));
    transition: width 500ms cubic-bezier(0.16, 1, 0.3, 1);

    &--done {
      background: linear-gradient(90deg, #46c8de, #00d084);
    }
  }

  &__modules {
    list-style: none;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__module-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
  }

  &__module-name {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.82rem;
    font-weight: 500;
  }

  &__module-done {
    color: rgb(var(--v-theme-success));
    font-size: 0.75rem;
  }

  &__module-count {
    font-size: 0.75rem;
    color: rgba(var(--v-theme-on-surface), 0.5);
  }
}

@media (prefers-reduced-motion: reduce) {
  .tpw__fill {
    transition: none;
  }
}
</style>
